import { useEffect, useRef, useState } from "react";
import {
  API_DATA,
  ApiCategory,
  ApiEndpoint,
  ApiParam,
  ApiResponseField,
  ApiResponseType,
} from "./data";
import config from "../../shared/config/config.dev";
import "./index.scss";
import { Box, Dropdown, DropdownItem } from "fds";

const LS = {
  TOKEN: "api-ref-token",
  ADMIN_TOKEN: "api-ref-admin-token",
  DSD_CODE: "api-ref-dsd-code",
  GATEWAY: "api-ref-gateway-url",
};

const METHOD_COLORS: Record<string, string> = {
  GET: "method-get",
  POST: "method-post",
  PUT: "method-put",
  DELETE: "method-delete",
  PATCH: "method-patch",
};

interface TryItState {
  loading: boolean;
  pathParams: Record<string, string>;
  bodyValues: Record<string, string>; // params 기반 입력값
  body: string; // params 없을 때 raw JSON textarea
  response: { status: number; ok: boolean; data: string } | null;
}

const buildBodyFromParams = (
  params: ApiParam[],
  values: Record<string, string>
): object => {
  const result: Record<string, any> = {};
  params.forEach((param) => {
    const raw = values[param.key] ?? "";
    if (!raw) return;
    switch (param.type) {
      case "number":
        result[param.key] = Number(raw);
        break;
      case "boolean":
        result[param.key] = raw === "true";
        break;
      case "string[]":
        result[param.key] = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        break;
      case "number[]":
        result[param.key] = raw
          .split(",")
          .map((s) => Number(s.trim()))
          .filter((n) => !isNaN(n));
        break;
      case "object":
        try {
          result[param.key] = JSON.parse(raw);
        } catch {
          result[param.key] = raw;
        }
        break;
      default:
        result[param.key] = raw;
    }
  });
  return result;
};

const extractPathParams = (endpoint: string): string[] => {
  const matches = endpoint.match(/\{(\w+)\}/g);
  return matches ? [...new Set(matches.map((m) => m.slice(1, -1)))] : [];
};

function ApiReference() {
  const [activeId, setActiveId] = useState<string>(API_DATA[0]?.id ?? "");
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [token, setToken] = useState(
    () =>
      localStorage.getItem("fasoo-eco-token") ??
      localStorage.getItem(LS.TOKEN) ??
      ""
  );
  const [adminToken, setAdminToken] = useState(
    () => localStorage.getItem(LS.ADMIN_TOKEN) ?? ""
  );
  const [dsdCode, setDsdCode] = useState(
    () =>
      sessionStorage.getItem("fasoo-workspace-code") ??
      localStorage.getItem(LS.DSD_CODE) ??
      ""
  );
  const [gatewayUrl, setGatewayUrl] = useState(
    () => localStorage.getItem(LS.GATEWAY) ?? config.servers.gateway.url
  );

  useEffect(() => {
    localStorage.setItem(LS.TOKEN, token);
  }, [token]);
  useEffect(() => {
    localStorage.setItem(LS.ADMIN_TOKEN, adminToken);
  }, [adminToken]);
  useEffect(() => {
    localStorage.setItem(LS.DSD_CODE, dsdCode);
  }, [dsdCode]);
  useEffect(() => {
    localStorage.setItem(LS.GATEWAY, gatewayUrl);
  }, [gatewayUrl]);
  const [configOpen, setConfigOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [tryItStates, setTryItStates] = useState<Record<string, TryItState>>(
    {}
  );
  const [showVersion, setShowVersion] = useState("전체");

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { root: container, rootMargin: "-10% 0px -70% 0px", threshold: 0 }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    const container = containerRef.current;
    if (!el || !container) return;
    const HEADER_HEIGHT = 80;
    const elTop =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollBy({ top: elTop - HEADER_HEIGHT - 8, behavior: "smooth" });
  };

  const getState = (key: string): TryItState =>
    tryItStates[key] ?? {
      loading: false,
      pathParams: {},
      bodyValues: {},
      body: "",
      response: null,
    };

  const patchState = (key: string, patch: Partial<TryItState>) =>
    setTryItStates((prev) => ({
      ...prev,
      [key]: { ...getState(key), ...patch },
    }));

  const toggleExpand = (key: string) =>
    setExpandedKey((prev) => (prev === key ? null : key));

  const execute = async (
    key: string,
    method: string,
    endpoint: string,
    section: "user" | "admin",
    params?: ApiParam[],
    responseType?: ApiResponseType
  ) => {
    const state = getState(key);

    let url = endpoint;
    extractPathParams(endpoint).forEach((param) => {
      const val = state.pathParams[param];
      if (val)
        url = url.replace(
          new RegExp(`\\{${param}\\}`, "g"),
          encodeURIComponent(val)
        );
    });

    const fullUrl = `${gatewayUrl}${url}`;
    const usedToken = section === "admin" ? adminToken || token : token;

    const headers: Record<string, string> = dsdCode
      ? {
          "X-FASOO-API-TOKEN": usedToken,
          "X-FASOO-DSDCODE": dsdCode,
          "Content-Type": "application/json",
          locale: "kr-KR",
          "Accept-Language": "ko",
        }
      : {
          "X-FASOO-API-TOKEN": usedToken,
          "Content-Type": "application/json",
          locale: "kr-KR",
          "Accept-Language": "ko",
        };

    const options: RequestInit = { method, headers };
    if (["POST", "PUT", "PATCH"].includes(method)) {
      if (params && params.length > 0) {
        options.body = JSON.stringify(
          buildBodyFromParams(params, state.bodyValues)
        );
      } else if (state.body.trim()) {
        options.body = state.body;
      }
    }
    options.credentials = "include";

    patchState(key, { loading: true, response: null });

    try {
      const res = await fetch(fullUrl, options);
      let data: string;
      const ct = res.headers.get("content-type") ?? "";
      const resolvedType =
        responseType ?? (ct.includes("application/json") ? "json" : "text");
      if (resolvedType === "blob") {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        data = `[blob] size=${blob.size} type=${blob.type}\nURL: ${url}`;
      } else if (resolvedType === "json" || ct.includes("application/json")) {
        const json = await res.json();
        data = JSON.stringify(json, null, 2);
      } else {
        data = await res.text();
      }
      patchState(key, {
        loading: false,
        response: { status: res.status, ok: res.ok, data },
      });
    } catch (err: any) {
      patchState(key, {
        loading: false,
        response: {
          status: 0,
          ok: false,
          data: `Network Error: ${err.message}`,
        },
      });
    }
  };

  const userCategories = API_DATA.filter((c) => c.section === "user");
  const adminCategories = API_DATA.filter((c) => c.section === "admin");
  const totalCount = API_DATA.reduce((sum, c) => sum + c.apis.length, 0);

  const authReady =
    token.length > 0 &&
    (window.eco.serviceType !== "OCI" || dsdCode.length > 0);

  return (
    <div className="api-reference" ref={containerRef}>
      {/* 헤더 */}
      <header className="api-reference__header">
        <div className="api-reference__header-inner">
          <div className="api-reference__header-left">
            <img
              src={`${process.env.PUBLIC_URL}/logo_wrapsody_eco.png`}
              alt="Wrapsody eCo"
              className="api-reference__logo"
            />
            <span className="api-reference__header-divider" />
            <div className="api-reference__header-title">
              <span className="api-reference__header-label">API Reference</span>
              <span className="api-reference__total-badge">
                {totalCount} endpoints
              </span>
            </div>
          </div>
          <div className="api-reference__header-right">
            <span
              className={`api-reference__auth-status ${
                authReady ? "is-ready" : ""
              }`}
            >
              {authReady ? "● 인증됨" : "○ 미인증"}
            </span>
            <button
              className="api-reference__config-btn"
              onClick={() => setConfigOpen((v) => !v)}
            >
              {configOpen ? "설정 닫기" : "인증 설정"}
            </button>
            <Dropdown
              align="right"
              width={100}
              value={showVersion}
              classes="dropdwon-versoin"
              style={{ background: "rgba(147, 197, 253, 0.1)", height: "26px" }}
              buttonClick={parseFloat(showVersion)}
              placeholder=" "
            >
              <DropdownItem
                label="전체"
                onClick={() => setShowVersion("전체")}
              />
              <DropdownItem label="3.0" onClick={() => setShowVersion("3.0")} />
              <DropdownItem
                label="~2.9"
                onClick={() => setShowVersion("~2.9")}
              />
            </Dropdown>
          </div>
        </div>

        {/* 인증 설정 패널 */}
        {configOpen && (
          <div className="api-reference__config-panel">
            <div className="api-reference__config-inner">
              <div className="api-reference__config-field">
                <label>Gateway URL</label>
                <input
                  value={gatewayUrl}
                  onChange={(e) => setGatewayUrl(e.target.value)}
                  spellCheck={false}
                />
              </div>
              <div className="api-reference__config-field">
                <label>
                  Token <span>(사용자)</span>
                </label>
                <div className="api-reference__config-input-row">
                  <input
                    type="password"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="fasoo-eco-token"
                    spellCheck={false}
                  />
                  <button
                    onClick={() =>
                      setToken(
                        localStorage.getItem("fasoo-eco-token") ??
                          localStorage.getItem(LS.TOKEN) ??
                          ""
                      )
                    }
                  >
                    자동입력
                  </button>
                </div>
              </div>
              <div className="api-reference__config-field">
                <label>
                  Token <span>(관리자)</span>
                </label>
                <div className="api-reference__config-input-row">
                  <input
                    type="password"
                    value={adminToken}
                    onChange={(e) => setAdminToken(e.target.value)}
                    placeholder="fasoo-eco-admin-token"
                    spellCheck={false}
                  />
                  <button
                    onClick={() =>
                      setAdminToken(
                        localStorage.getItem("fasoo-eco-admin-token") ??
                          localStorage.getItem(LS.ADMIN_TOKEN) ??
                          ""
                      )
                    }
                  >
                    자동입력
                  </button>
                </div>
              </div>
              <div className="api-reference__config-field">
                <label>
                  DSD Code <span>(워크스페이스)</span>
                </label>
                <div className="api-reference__config-input-row">
                  <input
                    value={dsdCode}
                    onChange={(e) => setDsdCode(e.target.value)}
                    placeholder="fasoo-workspace-code"
                    spellCheck={false}
                  />
                  <button
                    onClick={() =>
                      setDsdCode(
                        sessionStorage.getItem("fasoo-workspace-code") ??
                          localStorage.getItem(LS.DSD_CODE) ??
                          ""
                      )
                    }
                  >
                    자동입력
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="api-reference__body">
        {/* 사이드바 */}
        <nav className="api-reference__sidebar">
          <div className="api-reference__sidebar-section">
            <p className="api-reference__sidebar-group-label">사용자</p>
            {userCategories.map((cat) => (
              <button
                key={cat.id}
                className={`api-reference__sidebar-item ${
                  activeId === cat.id ? "is-active" : ""
                }`}
                onClick={() => scrollTo(cat.id)}
              >
                {cat.label}
                <span className="api-reference__sidebar-count">
                  {
                    cat.apis.filter(
                      (api: ApiEndpoint) =>
                        api.version === showVersion || showVersion === "전체"
                    ).length
                  }
                </span>
              </button>
            ))}
          </div>
          <div className="api-reference__sidebar-section">
            <p className="api-reference__sidebar-group-label">관리자</p>
            {adminCategories.map((cat) => (
              <button
                key={cat.id}
                className={`api-reference__sidebar-item ${
                  activeId === cat.id ? "is-active" : ""
                }`}
                onClick={() => scrollTo(cat.id)}
              >
                {cat.label}
                <span className="api-reference__sidebar-count">
                  {
                    cat.apis.filter(
                      (api: ApiEndpoint) =>
                        api.version === showVersion || showVersion === "전체"
                    ).length
                  }
                </span>
              </button>
            ))}
          </div>
        </nav>

        {/* 메인 콘텐츠 */}
        <main className="api-reference__content">
          {API_DATA.map((category: ApiCategory) => (
            <section
              key={category.id}
              id={category.id}
              ref={(el) => {
                sectionRefs.current[category.id] = el;
              }}
              className="api-reference__section"
            >
              <div className="api-reference__section-header">
                <span
                  className={`api-reference__section-badge api-reference__section-badge--${category.section}`}
                >
                  {category.section === "user" ? "사용자" : "관리자"}
                </span>
                <h2>{category.label} API</h2>
              </div>

              <div className="api-reference__endpoint-list">
                {category.apis
                  .filter(
                    (api: ApiEndpoint) =>
                      api.version === showVersion || showVersion === "전체"
                  )
                  .map((api: ApiEndpoint, idx: number) => {
                    const key = `${category.id}__${idx}`;
                    const isOpen = expandedKey === key;
                    const state = getState(key);
                    const pathParams = extractPathParams(api.endpoint);
                    const hasBody = ["POST", "PUT", "PATCH"].includes(
                      api.method
                    );
                    const hasDefinedParams =
                      hasBody && api.params && api.params.length > 0;

                    return (
                      <div
                        key={key}
                        className={`api-reference__endpoint ${
                          isOpen ? "is-open" : ""
                        }`}
                      >
                        {/* 엔드포인트 행 */}
                        <button
                          className="api-reference__endpoint-row"
                          onClick={() => toggleExpand(key)}
                        >
                          <span
                            className={`api-reference__method-badge ${
                              METHOD_COLORS[api.method] ?? ""
                            }`}
                          >
                            {api.method}
                          </span>
                          <div className="api-reference__endpoint-info">
                            <span className="api-reference__endpoint-name">
                              {api.name}
                            </span>
                            <code className="api-reference__endpoint-url">
                              {api.endpoint}
                            </code>
                          </div>
                          <span className="api-reference__endpoint-arrow">
                            {isOpen ? "▲" : "▼"}
                          </span>
                        </button>

                        {/* Try it 패널 */}
                        {isOpen && (
                          <div className="api-reference__tryit">
                            {/* 파라미터 설명 테이블 */}
                            {(pathParams.length > 0 ||
                              (api.params && api.params.length > 0)) && (
                              <div className="api-reference__tryit-section">
                                <p className="api-reference__tryit-label">
                                  Parameters
                                </p>
                                <table className="api-reference__params-table">
                                  <thead>
                                    <tr>
                                      <th>이름</th>
                                      <th>타입</th>
                                      <th>필수</th>
                                      <th>설명</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pathParams.map((param) => (
                                      <tr key={`path-${param}`}>
                                        <td>
                                          <code>{param}</code>
                                        </td>
                                        <td>
                                          <span className="api-reference__param-type">
                                            string
                                          </span>
                                        </td>
                                        <td>
                                          <span className="api-reference__param-required">
                                            O
                                          </span>
                                        </td>
                                        <td className="api-reference__param-desc">
                                          Path parameter
                                        </td>
                                      </tr>
                                    ))}
                                    {api.params?.map((param) => (
                                      <tr key={param.key}>
                                        <td>
                                          <code>{param.key}</code>
                                        </td>
                                        <td>
                                          <span className="api-reference__param-type">
                                            {param.type}
                                          </span>
                                        </td>
                                        <td>
                                          {param.required ? (
                                            <span className="api-reference__param-required">
                                              O
                                            </span>
                                          ) : (
                                            <span className="api-reference__param-optional">
                                              X
                                            </span>
                                          )}
                                        </td>
                                        <td className="api-reference__param-desc">
                                          {param.description ?? "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Response 필드 테이블 */}
                            {api.response && api.response.length > 0 && (
                              <div className="api-reference__tryit-section">
                                <p className="api-reference__tryit-label">
                                  Response
                                </p>
                                <table className="api-reference__params-table">
                                  <thead>
                                    <tr>
                                      <th>이름</th>
                                      <th>타입</th>
                                      <th>설명</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {api.response.map(
                                      (field: ApiResponseField) => {
                                        const parts = field.key.split(".");
                                        const depth = parts.length - 1;
                                        const displayKey =
                                          parts[parts.length - 1];
                                        return (
                                          <tr key={field.key}>
                                            <td>
                                              <span
                                                style={{
                                                  paddingLeft: `${
                                                    depth * 14
                                                  }px`,
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                }}
                                              >
                                                {depth > 0 && (
                                                  <span
                                                    style={{
                                                      color: "#9ca3af",
                                                      fontSize: "11px",
                                                      userSelect: "none",
                                                    }}
                                                  >
                                                    └
                                                  </span>
                                                )}
                                                <code>{displayKey}</code>
                                              </span>
                                            </td>
                                            <td>
                                              <span className="api-reference__param-type">
                                                {field.type}
                                              </span>
                                            </td>
                                            <td className="api-reference__param-desc">
                                              {field.description ?? "-"}
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {/* Path Params */}
                            {pathParams.length > 0 && (
                              <div className="api-reference__tryit-section">
                                <p className="api-reference__tryit-label">
                                  Path Parameters
                                </p>
                                <div className="api-reference__tryit-params">
                                  {pathParams.map((param) => (
                                    <div
                                      key={param}
                                      className="api-reference__tryit-param"
                                    >
                                      <label>{param}</label>
                                      <input
                                        value={state.pathParams[param] ?? ""}
                                        onChange={(e) =>
                                          patchState(key, {
                                            pathParams: {
                                              ...state.pathParams,
                                              [param]: e.target.value,
                                            },
                                          })
                                        }
                                        placeholder={`{${param}}`}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Request Body */}
                            {hasBody && (
                              <div className="api-reference__tryit-section">
                                <p className="api-reference__tryit-label">
                                  Request Body
                                </p>
                                {hasDefinedParams ? (
                                  <div className="api-reference__tryit-params">
                                    {api.params!.map((param) => (
                                      <div
                                        key={param.key}
                                        className="api-reference__tryit-param"
                                      >
                                        <div className="api-reference__tryit-param-label">
                                          <span className="name">
                                            {param.key}
                                            {param.required && (
                                              <span className="required">
                                                *
                                              </span>
                                            )}
                                          </span>
                                          <span className="type">
                                            {param.type}
                                          </span>
                                        </div>
                                        {param.type === "boolean" ? (
                                          <select
                                            value={
                                              state.bodyValues[param.key] ?? ""
                                            }
                                            onChange={(e) =>
                                              patchState(key, {
                                                bodyValues: {
                                                  ...state.bodyValues,
                                                  [param.key]: e.target.value,
                                                },
                                              })
                                            }
                                          >
                                            <option value="">선택</option>
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                          </select>
                                        ) : param.type === "object" ? (
                                          <textarea
                                            className="api-reference__tryit-body"
                                            rows={3}
                                            value={
                                              state.bodyValues[param.key] ?? ""
                                            }
                                            onChange={(e) =>
                                              patchState(key, {
                                                bodyValues: {
                                                  ...state.bodyValues,
                                                  [param.key]: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder="{}"
                                            spellCheck={false}
                                          />
                                        ) : (
                                          <input
                                            value={
                                              state.bodyValues[param.key] ?? ""
                                            }
                                            onChange={(e) =>
                                              patchState(key, {
                                                bodyValues: {
                                                  ...state.bodyValues,
                                                  [param.key]: e.target.value,
                                                },
                                              })
                                            }
                                            placeholder={
                                              param.type.includes("[]")
                                                ? "value1, value2, ..."
                                                : param.type
                                            }
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <textarea
                                    className="api-reference__tryit-body"
                                    value={state.body}
                                    onChange={(e) =>
                                      patchState(key, { body: e.target.value })
                                    }
                                    placeholder="{}"
                                    rows={6}
                                    spellCheck={false}
                                  />
                                )}
                              </div>
                            )}

                            {/* 요청 헤더 미리보기 */}
                            <div className="api-reference__tryit-section">
                              <p className="api-reference__tryit-label">
                                Headers
                              </p>
                              <div className="api-reference__tryit-headers">
                                <span className="key">X-FASOO-API-TOKEN</span>
                                <span className="val">
                                  {token ? `${token.slice(0, 8)}…` : "(미설정)"}
                                </span>
                                <span className="key">X-FASOO-DSDCODE</span>
                                <span className="val">
                                  {dsdCode || "(미설정)"}
                                </span>
                              </div>
                            </div>

                            {/* 버전 / 응답 타입 */}
                            <div className="api-reference__tryit-section">
                              <Box alignItems="center">
                                <p className="api-reference__tryit-label">
                                  Version
                                </p>
                                <p
                                  className="api-reference__tryit-label mg ml-8"
                                  style={{ color: "#1d4ed8" }}
                                >
                                  {api.version}
                                </p>
                                <p
                                  className="api-reference__tryit-label mg ml-16"
                                  style={{ color: "#6b7280" }}
                                >
                                  Response
                                </p>
                                <p
                                  className="api-reference__tryit-label mg ml-8"
                                  style={{ color: "#059669" }}
                                >
                                  {api.responseType ?? "json"}
                                </p>
                              </Box>
                            </div>

                            {/* 실행 버튼 */}
                            <button
                              className="api-reference__tryit-execute"
                              onClick={() =>
                                execute(
                                  key,
                                  api.method,
                                  api.endpoint,
                                  category.section,
                                  api.params,
                                  api.responseType
                                )
                              }
                              disabled={state.loading}
                            >
                              {state.loading ? "요청 중…" : "실행"}
                            </button>

                            {/* 응답 */}
                            {state.response && (
                              <div className="api-reference__response">
                                <div className="api-reference__response-header">
                                  <span
                                    className={`api-reference__response-status ${
                                      state.response.ok ? "is-ok" : "is-err"
                                    }`}
                                  >
                                    {state.response.status === 0
                                      ? "Network Error"
                                      : `${state.response.status}`}
                                  </span>
                                  <span className="api-reference__response-label">
                                    Response
                                  </span>
                                </div>
                                <pre className="api-reference__response-body">
                                  {state.response.data}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}

export default ApiReference;
